export type PanelWidthChoice = "wide" | "compact";

export type PanelPlacement =
  | { kind: "beside"; widthPx: number; menu: "full" | "icons" }
  | { kind: "over"; widthPx: number }
  | { kind: "fullscreen" };

export const PANEL_WIDTH_PX: Record<PanelWidthChoice, number> = { wide: 512, compact: 400 };
export const PANEL_OVER_WIDTH_PX = 400;
export const MIN_PAGE_WIDTH_PX = 720;
export const MIN_TIGHT_PAGE_WIDTH_PX = 600;
export const MENU_FULL_WIDTH_PX = 288;
export const MENU_ICONS_WIDTH_PX = 72;

const MD_EM = 48;
const LG_EM = 62;

const pageMarginsPx = (windowWidth: number, remPx: number) => {
  if (windowWidth >= LG_EM * remPx) return 4 * remPx;
  if (windowWidth >= MD_EM * remPx) return 3 * remPx;
  return 2 * remPx;
};

export type PlacementInput = {
  windowWidth: number;
  remPx: number;
  choice: PanelWidthChoice;
  menuHidden: boolean;
  hasShell: boolean;
  ownSideColumnFromEm: number | null;
};

export const panelFillsTheScreen = (windowWidth: number, remPx: number): boolean => windowWidth < MD_EM * remPx;

export const placementFor = ({
  windowWidth,
  remPx,
  choice,
  menuHidden,
  hasShell,
  ownSideColumnFromEm,
}: PlacementInput): PanelPlacement => {
  if (panelFillsTheScreen(windowWidth, remPx)) return { kind: "fullscreen" };
  const over: PanelPlacement = { kind: "over", widthPx: PANEL_OVER_WIDTH_PX };
  const pageShowsOwnSideColumn = ownSideColumnFromEm !== null && windowWidth >= ownSideColumnFromEm * remPx;
  if (!hasShell || pageShowsOwnSideColumn) return over;

  const menuShown = windowWidth >= LG_EM * remPx;
  const menuPx = (menu: "full" | "icons") =>
    !menuShown ? 0 : menu === "full" ? MENU_FULL_WIDTH_PX : MENU_ICONS_WIDTH_PX;
  const margins = pageMarginsPx(windowWidth, remPx);
  const candidates: { menu: "full" | "icons"; widthPx: number }[] = [
    ...(menuHidden ? [] : [{ menu: "full" as const, widthPx: PANEL_WIDTH_PX[choice] }]),
    { menu: "icons", widthPx: PANEL_WIDTH_PX[choice] },
    { menu: "icons", widthPx: PANEL_WIDTH_PX.compact },
  ];
  const pageWidth = ({ menu, widthPx }: { menu: "full" | "icons"; widthPx: number }) =>
    windowWidth - menuPx(menu) - margins - widthPx;
  const tightest = { menu: "icons" as const, widthPx: PANEL_WIDTH_PX.compact };
  const fitting =
    candidates.find((candidate) => pageWidth(candidate) >= MIN_PAGE_WIDTH_PX) ??
    (pageWidth(tightest) >= MIN_TIGHT_PAGE_WIDTH_PX ? tightest : undefined);
  return fitting ? { kind: "beside", ...fitting } : over;
};

export const widthSwitchApplies = (input: Omit<PlacementInput, "choice">): boolean => {
  const width = (choice: PanelWidthChoice) => {
    const placement = placementFor({ ...input, choice });
    return placement.kind === "fullscreen" ? null : placement.widthPx;
  };
  return width("wide") !== width("compact");
};
